/** biome-ignore-all lint/suspicious/noEmptyBlockStatements: plugin */
import intlTelInput, { type Iti, type SomeOptions } from 'intl-tel-input'
import type React from 'react'
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'

// make this available as a named export, so react users can access globals like intlTelInput.utils
export { intlTelInput }

type ItiProps = {
  initialValue?: string
  onChangeNumber?: (number: string) => void
  onChangeCountry?: (country: string) => void
  onChangeValidity?: (valid: boolean, value: string) => void
  onChangeErrorCode?: (errorCode: number | null) => void
  usePreciseValidation?: boolean
  initOptions?: SomeOptions
  inputProps?: object
  disabled?: boolean | undefined
}

export type IntlTelInputRef = {
  getInstance: () => Iti | null
  getInput: () => HTMLInputElement | null
}

const IntlTelInput = memo(
  forwardRef(function IntlTelInput(
    {
      initialValue = '',
      onChangeNumber = () => {},
      onChangeCountry = () => {},
      onChangeValidity = () => {},
      onChangeErrorCode = () => {},
      usePreciseValidation = false,
      initOptions = {},
      inputProps = {},
      disabled = undefined,
    }: ItiProps,
    ref: React.ForwardedRef<IntlTelInputRef>,
  ) {
    const inputRef = useRef<HTMLInputElement | null>(null)
    const itiRef = useRef<Iti | null>(null)

    // expose the instance and input ref to the parent component
    useImperativeHandle(ref, () => ({
      getInstance: () => itiRef.current,
      getInput: () => inputRef.current,
    }))

    const update = useCallback((): void => {
      const num = itiRef.current?.getNumber() || ''
      const countryIso = itiRef.current?.getSelectedCountryData().iso2 || ''
      // note: this number will be in standard E164 format, but any container component can use
      // intlTelInput.utils.formatNumber() to convert this to another format
      // as well as intlTelInput.utils.getNumberType() etc. if need be
      onChangeNumber(num)
      onChangeCountry(countryIso)

      if (itiRef.current) {
        const isValid = usePreciseValidation
          ? itiRef.current.isValidNumberPrecise()
          : itiRef.current.isValidNumber()
        if (isValid) {
          onChangeValidity(true, num)
          onChangeErrorCode(null)
        } else {
          const errorCode = itiRef.current.getValidationError()
          onChangeValidity(false, num)
          onChangeErrorCode(errorCode)
        }
      }
    }, [
      onChangeCountry,
      onChangeErrorCode,
      onChangeNumber,
      onChangeValidity,
      usePreciseValidation,
    ])

    // biome-ignore lint/correctness/useExhaustiveDependencies: plugin
    useEffect(() => {
      if (inputRef.current) {
        itiRef.current = intlTelInput(inputRef.current, initOptions)
        itiRef.current.promise.then(update)
      }
      return (): void => {
        itiRef.current?.destroy()
      }
    }, [])

    useEffect(() => {
      // store a reference to the current input ref, which otherwise is already lost in the cleanup function
      const inputRefCurrent = inputRef.current
      if (inputRefCurrent) {
        inputRefCurrent.addEventListener('countrychange', update)
        // when plugin initialisation has finished (e.g. loaded utils script), update all the state values
      }
      return (): void => {
        if (inputRefCurrent) {
          inputRefCurrent.removeEventListener('countrychange', update)
        }
      }
    }, [update])

    useEffect(() => {
      if (itiRef.current && disabled !== undefined) {
        itiRef.current.setDisabled(disabled)
      }
    }, [disabled])

    return (
      <input
        type="tel"
        ref={inputRef}
        onInput={update}
        defaultValue={initialValue}
        {...inputProps}
      />
    )
  }),
)

// biome-ignore lint/style/noDefaultExport: plugin
export default IntlTelInput
